import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Loader2, Image as ImageIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "../components/AdPlacement";

// Set worker path
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;

function PdfToImages() {
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [format, setFormat] = useState("png");
  const [quality, setQuality] = useState(90);
  const [scale, setScale] = useState(2);
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "PDF to Images - Convert PDF Pages to Images - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setPages([]);
      await loadPdfPreview(selectedFile);
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setPages([]);
      await loadPdfPreview(droppedFile);
    } else {
      toast.error("Please drop a valid PDF file");
    }
  };

  const loadPdfPreview = async (pdfFile) => {
    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      setTotalPages(pdf.numPages);
      toast.success(`PDF loaded: ${pdf.numPages} pages`);
    } catch (error) {
      console.error("Error loading PDF:", error);
      toast.error("Failed to load PDF");
    }
  };

  const convertToImages = async () => {
    if (!file) {
      toast.error("Please select a PDF file first");
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setPages([]);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const numPages = pdf.numPages;
      const convertedPages = [];

      for (let i = 1; i <= numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: scale });
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;

        const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
        const qualityValue = format === 'png' ? 1 : quality / 100;
        
        const dataUrl = canvas.toDataURL(mimeType, qualityValue);
        
        convertedPages.push({
          pageNum: i,
          dataUrl: dataUrl,
          width: viewport.width,
          height: viewport.height
        });

        setProgress(Math.round((i / numPages) * 100));
      }

      setPages(convertedPages);
      toast.success(`Converted ${numPages} pages to images!`);
    } catch (error) {
      console.error("Conversion error:", error);
      toast.error("Failed to convert PDF: " + error.message);
    }

    setIsProcessing(false);
  };

  const downloadSingle = (page) => {
    const link = document.createElement('a');
    link.href = page.dataUrl;
    link.download = `${file.name.replace('.pdf', '')}_page_${page.pageNum}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAll = async () => {
    if (pages.length === 0) return;

    if (pages.length === 1) {
      downloadSingle(pages[0]);
      return;
    }

    const zip = new JSZip();
    
    for (const page of pages) {
      const base64Data = page.dataUrl.split(',')[1];
      zip.file(`page_${page.pageNum}.${format}`, base64Data, { base64: true });
    }

    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, `${file.name.replace('.pdf', '')}_images.zip`);
  };

  const clearFile = () => {
    setFile(null);
    setPages([]);
    setTotalPages(0);
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-blue-50 to-indigo-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full mb-4">
            <ImageIcon className="w-5 h-5" />
            <span className="font-medium">PDF to Images</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Convert PDF to Images
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Extract each page from your PDF as high-quality PNG or JPG images.
          </p>
        </div>

        {/* Upload Area */}
        <Card 
          className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 max-w-2xl mx-auto ${
            isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-300 dark:border-slate-600'
          }`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <CardContent className="p-12 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
            {!file ? (
              <div 
                className="cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-16 h-16 mx-auto mb-4 text-blue-500" />
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  Drop PDF or click to upload
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Select a PDF file to convert to images
                </p>
              </div>
            ) : (
              <div>
                <FileText className="w-16 h-16 mx-auto mb-4 text-blue-500" />
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  {file.name}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-4">
                  {totalPages} pages
                </p>
                <Button variant="outline" size="sm" onClick={clearFile}>
                  <X className="w-4 h-4 mr-1" /> Remove
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {file && (
          <div className="max-w-4xl mx-auto mt-6 space-y-6">
            {/* Settings */}
            <Card className="dark:bg-slate-800">
              <CardContent className="p-6">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Format</label>
                    <div className="flex gap-2">
                      <Button
                        variant={format === "png" ? "default" : "outline"}
                        onClick={() => setFormat("png")}
                        className={format === "png" ? "bg-blue-600" : ""}
                        size="sm"
                      >
                        PNG
                      </Button>
                      <Button
                        variant={format === "jpg" ? "default" : "outline"}
                        onClick={() => setFormat("jpg")}
                        className={format === "jpg" ? "bg-blue-600" : ""}
                        size="sm"
                      >
                        JPG
                      </Button>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Scale: {scale}x</label>
                    <div className="flex gap-2">
                      {[1, 2, 3].map(s => (
                        <Button
                          key={s}
                          variant={scale === s ? "default" : "outline"}
                          onClick={() => setScale(s)}
                          className={scale === s ? "bg-blue-600" : ""}
                          size="sm"
                        >
                          {s}x
                        </Button>
                      ))}
                    </div>
                  </div>
                  {format === "jpg" && (
                    <div>
                      <label className="text-sm font-medium mb-2 block">Quality: {quality}%</label>
                      <input
                        type="range"
                        min="50"
                        max="100"
                        value={quality}
                        onChange={(e) => setQuality(parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {isProcessing && (
              <Card className="dark:bg-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-2">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                    <span className="text-sm">Converting pages...</span>
                  </div>
                  <Progress value={progress} className="h-3" />
                </CardContent>
              </Card>
            )}

            {!pages.length && !isProcessing && (
              <div className="text-center">
                <Button 
                  onClick={convertToImages}
                  size="lg"
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:opacity-90"
                >
                  <ImageIcon className="w-5 h-5 mr-2" />
                  Convert to Images
                </Button>
              </div>
            )}

            {pages.length > 0 && (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold dark:text-white">
                    Converted Pages ({pages.length})
                  </h3>
                  <Button 
                    onClick={downloadAll}
                    className="bg-gradient-to-r from-green-500 to-green-600"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download All (ZIP)
                  </Button>
                </div>

                <DesktopOnlyAd slot="above-results" size="large-rectangle" className="my-4" />

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {pages.map((page) => (
                    <Card key={page.pageNum} className="dark:bg-slate-800 overflow-hidden group">
                      <div className="aspect-[3/4] relative bg-slate-100 dark:bg-slate-700">
                        <img
                          src={page.dataUrl}
                          alt={`Page ${page.pageNum}`}
                          className="w-full h-full object-contain"
                        />
                        <Badge className="absolute top-2 left-2 bg-black/70">
                          Page {page.pageNum}
                        </Badge>
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Button size="sm" onClick={() => downloadSingle(page)}>
                            <Download className="w-4 h-4 mr-1" /> Download
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default PdfToImages;
